import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ThemeIcon, Loader } from '@mantine/core';
import {
    IconCalendarCheck,
    IconRoute,
    IconSquare0,
    IconShoppingCart,
    IconCalendarClock,
} from '@tabler/icons-react';
import dayjs from 'dayjs';

import {
    useCreateReservation,
    useCreateServiceOrder,
    useAssignedBookings,
    useAssignedServiceOrders
} from '@/hooks/api-hooks';
import {
    ASSIGNMENTS_PATH,
    RESERVATIONS_PATH,
    SERVICE_ORDERS_PATH,
    navigate
} from '@/lib/navigation';
import { RESERVATION_PAYMENT_STATUS, RESERVATION_STATUS } from '@turizi/shared';
import { localTenant } from '@/services/session-service';

import ModalNewTicket from '@/components/modal-new-ticket';
import CardModal from '@/components/card-modal';
import { BookingPaxsSize, ShowPaxGroup } from '@/components/snippets';
import { ServiceTitleStatic } from '@/components/services/service-title';
import { openPayingCustomersModal } from '@/components/paying-customers-only-modal';
import { isTier1Tenant } from '@/lib/format-utils';
import AssignmentsPage from './assignments-page';

interface ActionItem {
    label: string;
    description: string;
    icon: JSX.Element;
    component?: JSX.Element | null;
    link?: string;
    action?: () => void;
}

export default function HomePage(): JSX.Element {
    const { t } = useTranslation();
    const createReservation = useCreateReservation();
    const createServiceOrder = useCreateServiceOrder();

    const [selectedModal, setSelectedModal] = useState<ActionItem | null>(null);

    // Date Strings
    const todayStr = dayjs().format('YYYY-MM-DD');
    const tomorrowStr = dayjs().add(1, 'day').format('YYYY-MM-DD');
    const startOfWeekStr = dayjs().startOf('week').format('YYYY-MM-DD');
    const endOfWeekStr = dayjs().endOf('week').format('YYYY-MM-DD');

    // Query only for the current week / tomorrow range to avoid fetching the whole DB
    const queryParams = useMemo(() => {
        const start = startOfWeekStr;
        const end = dayjs(endOfWeekStr).isBefore(tomorrowStr) ? tomorrowStr : endOfWeekStr;
        return { start_day: start, end_day: end };
    }, [startOfWeekStr, endOfWeekStr, tomorrowStr]);

    // Fetch assignments for the query range to compute counts client-side
    const { data: res1, isLoading: loading1 } = useAssignedBookings(queryParams);
    const { data: res2, isLoading: loading2 } = useAssignedServiceOrders(queryParams);

    const bookings = res1?.data || [];
    const serviceOrders = res2?.data || [];
    const isLoadingActivity = loading1 || loading2;

    // Helper functions for matching
    const bookingMatchesDay = (booking: any, day: string) => {
        if (!booking.start_day) return false;
        const start = booking.start_day;
        const end = booking.end_day || start;
        return day >= start && day <= end;
    };

    const bookingMatchesRange = (booking: any, start: string, end: string) => {
        if (!booking.start_day) return false;
        const bStart = booking.start_day;
        const bEnd = booking.end_day || bStart;
        return bStart <= end && bEnd >= start;
    };

    const orderMatchesDay = (order: any, day: string) => {
        const bookings = [
            ...(order.service_bookings || []),
            ...(order.service_order_bookings?.map((sob: any) => sob.service_booking).filter(Boolean) || [])
        ];
        return bookings.some(b => bookingMatchesDay(b, day));
    };

    const orderMatchesRange = (order: any, start: string, end: string) => {
        const bookings = [
            ...(order.service_bookings || []),
            ...(order.service_order_bookings?.map((sob: any) => sob.service_booking).filter(Boolean) || [])
        ];
        return bookings.some(b => bookingMatchesRange(b, start, end));
    };

    const getServicePaxBreakdown = (dayBookings: any[]) => {
        const breakdown: {
            [serviceId: number]: {
                name: string;
                adults: number;
                children: number;
                seniors: number;
                infants: number;
                guests: number;
            }
        } = {};
        for (const b of dayBookings) {
            const serviceId = b.service_id;
            if (!breakdown[serviceId]) {
                breakdown[serviceId] = {
                    name: ServiceTitleStatic(b.service),
                    adults: 0,
                    children: 0,
                    seniors: 0,
                    infants: 0,
                    guests: 0
                };
            }
            breakdown[serviceId].adults += (b.adults || 0);
            breakdown[serviceId].children += (b.children || 0);
            breakdown[serviceId].seniors += (b.seniors || 0);
            breakdown[serviceId].infants += (b.infants || 0);
            breakdown[serviceId].guests += (b.guests || 0);
        }
        return Object.values(breakdown).filter(item =>
            (item.adults + item.children + item.seniors + item.infants + item.guests) > 0
        );
    };

    // Calculate Counts
    const todayCounts = useMemo(() => {
        if (isLoadingActivity) return { bookings: 0, orders: 0, breakdown: [] };
        const matchingBookings = bookings.filter(b => bookingMatchesDay(b, todayStr));
        const paxsCount = matchingBookings.reduce((sum, b) => sum + BookingPaxsSize(b), 0);
        const ordersCount = serviceOrders.filter(o => orderMatchesDay(o, todayStr)).length;
        const breakdown = getServicePaxBreakdown(matchingBookings);
        return { bookings: paxsCount, orders: ordersCount, breakdown };
    }, [bookings, serviceOrders, todayStr, isLoadingActivity]);

    const tomorrowCounts = useMemo(() => {
        if (isLoadingActivity) return { bookings: 0, orders: 0, breakdown: [] };
        const matchingBookings = bookings.filter(b => bookingMatchesDay(b, tomorrowStr));
        const paxsCount = matchingBookings.reduce((sum, b) => sum + BookingPaxsSize(b), 0);
        const ordersCount = serviceOrders.filter(o => orderMatchesDay(o, tomorrowStr)).length;
        const breakdown = getServicePaxBreakdown(matchingBookings);
        return { bookings: paxsCount, orders: ordersCount, breakdown };
    }, [bookings, serviceOrders, tomorrowStr, isLoadingActivity]);

    const weekCounts = useMemo(() => {
        if (isLoadingActivity) return { bookings: 0, orders: 0, breakdown: [] };
        const matchingBookings = bookings.filter(b => bookingMatchesRange(b, startOfWeekStr, endOfWeekStr));
        const paxsCount = matchingBookings.reduce((sum, b) => sum + BookingPaxsSize(b), 0);
        const ordersCount = serviceOrders.filter(o => orderMatchesRange(o, startOfWeekStr, endOfWeekStr)).length;
        const breakdown = getServicePaxBreakdown(matchingBookings);
        return { bookings: paxsCount, orders: ordersCount, breakdown };
    }, [bookings, serviceOrders, startOfWeekStr, endOfWeekStr, isLoadingActivity]);

    const createNewReservation = (status: string) => {
        const tenant = localTenant();
        if (!isTier1Tenant()) {
            openPayingCustomersModal();
            return;
        }
        createReservation.mutate({
            status,
            payment_status: RESERVATION_PAYMENT_STATUS.unpaid,
            seller_id: tenant?.businesses?.[0]?.id
        }, {
            onSuccess: (data) => {
                if (data?.data) {
                    navigate(`${RESERVATIONS_PATH}/${data.data.id}`);
                } else {
                    alert(t('error_creating_reservation'));
                }
            },
            onError: () => {
                alert(t('error_creating_reservation'));
            }
        });
    };

    const createNewServiceOrder = () => {
        if (!isTier1Tenant()) {
            openPayingCustomersModal();
            return;
        }
        createServiceOrder.mutate({}, {
            onSuccess: (data) => {
                if (data?.data) {
                    navigate(`${SERVICE_ORDERS_PATH}/${data.data.id}`);
                } else {
                    alert(t('error_creating_service_order'));
                }
            },
            onError: () => {
                alert(t('error_creating_service_order'));
            }
        });
    };

    const quickActions: ActionItem[] = [
        {
            label: t('self_reservation'),
            description: t('new_reservation_desc'),
            icon: <IconCalendarCheck size={28} stroke={1.5} className="text-teal-600" />,
            action: () => createNewReservation(RESERVATION_STATUS.confirmed.name)
        },
        {
            label: t('assignments'),
            description: t('assigments_desc'),
            icon: <IconCalendarClock size={28} stroke={1.5} className="text-blue-600" />,
            action: () => navigate(ASSIGNMENTS_PATH)
        },
        {
            label: t('service_order'),
            description: t('new_service_order_desc'),
            icon: <IconRoute size={28} stroke={1.5} className="text-indigo-600" />,
            action: () => createNewServiceOrder()
        },
        {
            label: t('task'),
            description: t('new_task_desc'),
            icon: <IconSquare0 size={28} stroke={1.5} className="text-violet-600" />,
            component: <ModalNewTicket onCreate={() => setSelectedModal(null)} />
        },
    ];

    const isMutating = createReservation.isPending || createServiceOrder.isPending;

    return (
        <div className="panel-content space-y-8">

            <div>
                <div className="text-2xl font-bold text-slate-900 mb-2">
                    {t('quick_actions')}
                </div>
                {/* Quick Actions Panel */}
                <div className="flex gap-4 flex-wrap">
                    {quickActions.map((item, index) => (
                        <div
                            key={index}
                            onClick={() => {
                                if (isMutating) return;
                                if (item.link) {
                                    navigate(item.link);
                                } else if (item.component) {
                                    setSelectedModal(item);
                                } else if (item.action) {
                                    item.action();
                                }
                            }}
                            className={`max-w-[300px] solid-box-border-hover p-4 flex gap-4 items-start cursor-pointer transition-all hover:bg-slate-50/50 ${isMutating ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            <div className="p-2.5 bg-slate-50 rounded-xl group-hover:bg-white transition-colors shrink-0">
                                {item.icon}
                            </div>
                            <div className="space-y-1 min-w-0">
                                <h3 className="text-sm font-bold text-slate-700 truncate group-hover:text-slate-900">
                                    {item.label}
                                </h3>
                                <p className="text-xs text-slate-400 leading-snug line-clamp-2">
                                    {item.description}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}